import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { IMG, PRODUCT_SLUGS } from "@/lib/assets";
import { SiteLogo } from "@/components/SiteLogo";
import { digitsOnly, gameIdError, waError, loadPlayer, clearPlayer, GAME_ID_LABEL } from "@/lib/member";
import { InfoDialog, SavedPlayerBar } from "@/components/member/FormHelpers";
import { ConfirmPurchaseDialog } from "@/pages/ConfirmPurchaseDialog";
import { useAppearance, siteImg } from "@/context/AppearanceContext";
import { useNickCheck, NickStatus, nickBlockMsg } from "@/components/member/NickCheck";

const pkgImage = (productId, pkg, custom = {}) => {
  if (pkg.image_url) return siteImg(pkg.image_url);
  if (productId === "royal_dream") return siteImg(custom.royal_dream) || IMG.rdCoin;
  return pkg.category === "Kartu Bingo" ? siteImg(custom.higgs_domino_bingo) || IMG.higgsCat : siteImg(custom.higgs_domino_domino) || IMG.higgsTile;
};

const Divider = () => (
  <div className="mx-auto flex w-32 items-center gap-2 text-[var(--jp-primary)]">
    <span className="h-px flex-1 bg-[var(--jp-primary)]" />
    <span className="text-[10px]">◆</span>
    <span className="h-px flex-1 bg-[var(--jp-primary)]" />
  </div>
);

const PackageCard = ({ productId, pkg, locked, onSelect }) => {
  const { pkg_images: custom } = useAppearance();
  return (
  <button
    type="button"
    onClick={onSelect}
    className={`relative overflow-hidden rounded-xl border border-[#4a3d1c] bg-black text-center shadow-[0_0_18px_rgba(229,184,74,0.15)] transition-transform hover:-translate-y-0.5 ${locked ? "opacity-60" : ""}`}
    data-testid={`package-${pkg.price}`}
  >
    {pkg.discount ? (
      <span className="absolute right-2 top-2 z-10 rounded-full border border-white/70 bg-[#c81e1e] px-2 py-0.5 text-[9px] font-extrabold text-white">-{pkg.discount}%</span>
    ) : null}
    <img src={pkgImage(productId, pkg, custom)} alt={pkg.label} loading="lazy" className="aspect-square w-full object-cover" data-testid={`package-img-${pkg.price}`} />
    <div className="p-2.5">
      <div className="kz-gold-btn rounded-md py-1.5 text-xs font-extrabold text-black">{pkg.label}</div>
      {pkg.original ? <div className="mt-1.5 text-[10px] font-bold text-zinc-500 line-through">IDR {pkg.original.toLocaleString("id-ID")}</div> : null}
      <div className="text-sm font-extrabold text-[var(--jp-primary)]">IDR {pkg.price.toLocaleString("id-ID")}</div>
    </div>
  </button>
  );
};

const PlayerForm = ({ p, productId, gameId, setGameId, wa, setWa, showErrors, nick }) => {
  const saved = loadPlayer();
  const [hasSaved, setHasSaved] = useState(Boolean(saved?.ids?.[productId] || saved?.wa));
  const idErr = gameId ? gameIdError(gameId) : "";
  const wErr = wa ? waError(wa) : "";
  return (
    <section className="mt-6 rounded-xl border border-[#6b5a2a] bg-[#0d0d0d] p-4 text-left">
      <h2 className="kz-display text-center text-base text-[var(--jp-primary)]">INFORMASI PEMAIN</h2>
      <div className="mt-3">
        {hasSaved ? (
          <SavedPlayerBar
            saved={`${saved?.ids?.[productId] || "-"} / ${saved?.wa || "-"}`}
            onUse={() => { setGameId(saved?.ids?.[productId] || ""); setWa(saved?.wa || ""); }}
            onClear={() => { clearPlayer(); setHasSaved(false); setGameId(""); setWa(""); }}
          />
        ) : null}
      </div>
      <label className="block text-xs font-extrabold text-white">{GAME_ID_LABEL[productId]}</label>
      <input value={gameId} onChange={(e) => setGameId(digitsOnly(e.target.value, 10))} placeholder={p.id_placeholder} inputMode="numeric" className="kz-dark-input mt-1 font-bold" data-testid="game-id-input" />
      {idErr || (showErrors && !gameId) ? <p className="mt-1 text-[11px] font-bold text-rose-400" data-testid="game-id-error">{GAME_ID_LABEL[productId]} {gameId ? idErr : "wajib diisi"}</p> : <NickStatus n={nick} />}
      <label className="mt-3 block text-xs font-extrabold text-white">Nomor WhatsApp</label>
      <input value={wa} onChange={(e) => setWa(digitsOnly(e.target.value, 15))} placeholder="08xxxxxxxxxx" inputMode="tel" className="kz-dark-input mt-1 font-bold" data-testid="wa-input" />
      {wErr || (showErrors && !wa) ? <p className="mt-1 text-[11px] font-bold text-rose-400" data-testid="wa-error">{waError(wa)}</p> : null}
    </section>
  );
};

export const TopupPage = () => {
  const { slug } = useParams();
  const productId = PRODUCT_SLUGS[slug];
  const { games_enabled: ge } = useSiteSettings();
  const saved = loadPlayer();
  const [data, setData] = useState(null);
  const [gameId, setGameId] = useState(saved?.ids?.[productId] || "");
  const [wa, setWa] = useState(saved?.wa || "");
  const [category, setCategory] = useState("Kartu Domino");
  const [info, setInfo] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [pkg, setPkg] = useState(null);
  const nick = useNickCheck(productId, gameId);

  useEffect(() => {
    if (productId) {
      api.get(`/catalog/${productId}`).then((r) => setData(r.data));
    }
  }, [productId]);

  const categories = useMemo(() => [...new Set((data?.packages || []).map((x) => x.category).filter(Boolean))], [data]);
  const visible = useMemo(() => (data?.packages || []).filter((x) => !categories.length || x.category === category), [data, categories, category]);

  if (!productId) return <Navigate to="/" replace />;
  if (ge?.[productId] === false) {
    return (
      <main className="relative z-10 mx-auto max-w-md px-4 pb-32 pt-16 text-center" data-testid="game-unavailable">
        <SiteLogo className="mx-auto h-24 w-24 object-contain" testid="unavailable-logo" />
        <h1 className="kz-display mt-4 text-2xl text-[var(--jp-primary)]">Game Sedang Tidak Tersedia</h1>
        <p className="mt-2 text-sm font-bold text-white/80">Top up game ini sementara ditutup. Silakan coba lagi nanti.</p>
        <Link to="/" className="kz-outline-btn mx-auto mt-6 max-w-xs" data-testid="unavailable-back-home">Kembali ke Beranda</Link>
      </main>
    );
  }
  if (!data) return <div className="grid min-h-[60vh] place-items-center text-sm text-zinc-500">Memuat…</div>;
  const p = data.product;
  const locked = Boolean(gameIdError(gameId) || waError(wa) || nickBlockMsg(nick));

  const select = (item) => {
    setShowErrors(true);
    if (!gameId) return setInfo(`${GAME_ID_LABEL[productId]} wajib diisi.`);
    if (gameIdError(gameId)) return setInfo(`${GAME_ID_LABEL[productId]} ${gameIdError(gameId)}.`);
    if (waError(wa)) return setInfo(`${waError(wa)}.`);
    if (nickBlockMsg(nick)) return setInfo(nickBlockMsg(nick));
    setPkg(item);
  };

  return (
    <main className="relative z-10 mx-auto max-w-lg px-4 pb-32 pt-6 text-center font-bold" data-testid={`product-page-${productId}`}>
      <SiteLogo className="mx-auto h-32 w-32 object-contain drop-shadow-[0_0_24px_rgba(229,184,74,0.35)]" testid="product-logo" />
      <div className="mt-4"><Divider /></div>
      <h1 className="kz-display kz-gold-text mt-3 text-4xl sm:text-5xl" data-testid="product-title">{p.title}</h1>
      <p className="mt-3 text-xs font-extrabold tracking-[0.2em] text-[#e8d9a8]">★ {p.subtitle} ★</p>
      <p className="mt-1 text-[11px] font-bold text-white/80">{p.tagline}</p>
      <div className="mt-3"><Divider /></div>

      <PlayerForm p={p} productId={productId} gameId={gameId} setGameId={setGameId} wa={wa} setWa={setWa} showErrors={showErrors} nick={nick} />

      <h2 className="kz-display mt-6 text-xl text-[var(--jp-primary)]">{productId === "higgs_domino" ? "PAKET HIGGS PREMIUM" : "PAKET PREMIUM"}</h2>
      {locked ? <p className="mt-1 text-[11px] font-bold text-zinc-400" data-testid="packages-locked-hint">Isi ID & nomor WhatsApp dulu untuk memilih paket.</p> : null}
      {categories.length ? (
        <div className="mt-3 flex justify-center gap-2">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`rounded-full px-3 py-1 text-[10px] font-extrabold ${c === category ? "bg-gradient-to-b from-[#ffb020] to-[#f08c00] text-black" : "border border-white/40 text-white"}`} data-testid={`category-${c.replace(" ", "-").toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>
      ) : null}
      <div className="mt-4 grid grid-cols-2 gap-3">
        {visible.map((item) => (
          <PackageCard key={item.price} productId={productId} pkg={item} locked={locked} onSelect={() => select(item)} />
        ))}
      </div>

      {info ? <InfoDialog message={info} onClose={() => setInfo("")} /> : null}
      {pkg ? <ConfirmPurchaseDialog product={p} gameId={gameId} wa={wa} pkg={pkg} nickname={nick.status === "valid" ? nick.nickname : ""} onClose={() => setPkg(null)} /> : null}
    </main>
  );
};
